import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from '../../Common/DashboardTemplate';
import {
    Button, CircularProgress, TextField, IconButton
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { v4 as uuid } from 'uuid';
import { ServiceLocator } from '../../../Services/ServiceLocator';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';

const getCurrentlySellingAssemblyId = (assemblyId, assemblies) => {
    const assembliesWithoutFinalOrder = assemblies.filter(assembly =>
        assembly.assembly && assembly.assembly.assemblyId === assemblyId
        && !assembly.assembly.finalOrderNumber
    );

    if (assembliesWithoutFinalOrder.length === 1) {
        return assembliesWithoutFinalOrder[0].assembly.id;
    }

    if (assembliesWithoutFinalOrder.length > 1) {
        const sortedAssemblies = [...assembliesWithoutFinalOrder].sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortedAssemblies[0].assembly.id;
    }

    return null;
};

const isAssemblyCurrentlySelling = (assembly, assemblies) => {
    if (!assembly || assembly.finalOrderNumber) return false;
    return getCurrentlySellingAssemblyId(assembly.assemblyId, assemblies) === assembly.id;
};

const compareSkuOptions = (a, b) => {
    const partIdCompare = String(a.partId || '').localeCompare(String(b.partId || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
    if (partIdCompare !== 0) return partIdCompare;
    const revisionA = a.revision || '';
    const revisionB = b.revision || '';
    return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
};

const tileStyle = {
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: 0,
    marginTop: 20,
    width: '100%',
    maxWidth: 1800,
    marginLeft: 'auto',
    marginRight: 'auto',
};

const styles = theme => ({
    lineRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
    },
});

const emptyCart = () => ({
    id: '',
    notes: '',
    expiresUtc: '',
    lineItems: [],
});

class EditCustomerCart extends Component {
    state = {
        loading: true,
        saving: false,
        refreshing: false,
        cart: emptyCart(),
        shareLink: '',
        skuOptions: [],
    };

    liveCartPollRef = null;

    componentDidMount() {
        this.loadSkuOptions().then(() => this.loadCart());
    }

    componentWillUnmount() {
        this.stopLiveCartPolling();
    }

    startLiveCartPolling = () => {
        this.stopLiveCartPolling();
        if (!this.state.cart?.isCustomerCart) return;
        this.liveCartPollRef = window.setInterval(() => {
            this.refreshLiveCart({ silent: true });
        }, 15000);
    };

    stopLiveCartPolling = () => {
        if (this.liveCartPollRef) {
            clearInterval(this.liveCartPollRef);
            this.liveCartPollRef = null;
        }
    };

    refreshLiveCart = ({ silent = false } = {}) => {
        const { cartId } = this.props.match.params;
        if (!cartId || cartId === 'new') return Promise.resolve();
        if (!silent) this.setState({ refreshing: true });

        return ServiceLocator.Instance.getProductListingService()
            .getCart(cartId)
            .then(cart => {
                this.setState({
                    cart: {
                        ...cart,
                        expiresUtc: cart.expiresUtc ? cart.expiresUtc.slice(0, 16) : '',
                        lineItems: cart.lineItems || [],
                    },
                    shareLink: cart.loadUrl || '',
                    refreshing: false,
                    loading: false,
                });
                if (cart.isCustomerCart) this.startLiveCartPolling();
            })
            .catch(err => {
                if (!silent) window.$alertError(err.message || 'Failed to refresh cart');
                this.setState({ refreshing: false, loading: false });
            });
    };

    loadSkuOptions = async () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const [assemblies, subAssemblies, parts] = await Promise.all([
            assemblyService.getAllAssembliesExpanded(),
            assemblyService.getAllSubAssembliesExpanded(),
            ServiceLocator.Instance.getPartService().getAllPartsExpanded(),
        ]);

        // Match Packaging / Sales dropdown shape (icons + description), include every SKU
        // (do not filter to currently selling / packaging only), then sort by PN + rev.
        const skuOptions = [
            ...assemblies.map(a => ({
                type: 'assembly',
                partId: a.assembly.assemblyId,
                revision: a.assembly.revision,
                description: a.assembly.description,
                currentlyPackaging: !!a.assembly.currentlyPackaging,
                currentlySelling: isAssemblyCurrentlySelling(a.assembly, assemblies),
                catalogProductType: 'Assembly',
                catalogProductId: a.assembly.id,
                label: `${a.assembly.currentlyPackaging ? '📦 ' : ''}${isAssemblyCurrentlySelling(a.assembly, assemblies) ? '🛒 ' : ''}${a.assembly.assemblyId} rev ${a.assembly.revision}`,
            })),
            ...subAssemblies.map(s => ({
                type: 'subassembly',
                partId: s.subAssembly.subAssemblyId,
                revision: s.subAssembly.revision,
                description: s.subAssembly.description,
                currentlyPackaging: !!s.subAssembly.currentlyPackaging,
                catalogProductType: 'Subassembly',
                catalogProductId: s.subAssembly.id,
                label: `${s.subAssembly.currentlyPackaging ? '📦 ' : ''}${s.subAssembly.subAssemblyId} rev ${s.subAssembly.revision}`,
            })),
            ...parts.map(p => ({
                type: 'part',
                partId: p.part.partId,
                revision: p.part.revision,
                description: p.part.description,
                part: p.part,
                catalogProductType: 'Part',
                catalogProductId: p.part.id,
                label: `${p.part.partId} rev ${p.part.revision}`,
            })),
        ].sort(compareSkuOptions);

        this.setState({ skuOptions });
    };

    loadCart = () => {
        const { cartId } = this.props.match.params;
        if (!cartId || cartId === 'new') {
            this.setState({ loading: false, cart: emptyCart(), shareLink: '' });
            return;
        }

        ServiceLocator.Instance.getProductListingService()
            .getCart(cartId)
            .then(cart => {
                this.setState({
                    cart: {
                        ...cart,
                        expiresUtc: cart.expiresUtc ? cart.expiresUtc.slice(0, 16) : '',
                        lineItems: cart.lineItems || [],
                    },
                    shareLink: cart.loadUrl || '',
                    loading: false,
                }, () => {
                    if (cart.isCustomerCart) this.startLiveCartPolling();
                });
            })
            .catch(err => {
                window.$alertError(err.message || 'Failed to load cart');
                this.setState({ loading: false });
            });
    };

    updateField = (field, value) => {
        this.setState(prev => ({ cart: { ...prev.cart, [field]: value } }));
    };

    addLine = () => {
        this.setState(prev => ({
            cart: {
                ...prev.cart,
                lineItems: [
                    ...prev.cart.lineItems,
                    {
                        id: uuid(),
                        catalogProductType: 'Assembly',
                        catalogProductId: '',
                        quantity: 1,
                        displayLabel: '',
                        sortOrder: prev.cart.lineItems.length,
                    },
                ],
            },
        }));
    };

    updateLine = (lineId, patch) => {
        this.setState(prev => ({
            cart: {
                ...prev.cart,
                lineItems: prev.cart.lineItems.map(li => (li.id === lineId ? { ...li, ...patch } : li)),
            },
        }));
    };

    removeLine = (lineId) => {
        this.setState(prev => ({
            cart: {
                ...prev.cart,
                lineItems: prev.cart.lineItems.filter(li => li.id !== lineId),
            },
        }));
    };

    save = async () => {
        const { cart } = this.state;
        const { cartId } = this.props.match.params;
        const isNew = !cartId || cartId === 'new';

        const payload = {
            notes: cart.notes || null,
            expiresUtc: cart.expiresUtc ? new Date(cart.expiresUtc).toISOString() : null,
            lineItems: cart.lineItems
                .filter(li => li.catalogProductId)
                .map((li, index) => ({
                    id: li.id,
                    catalogProductType: li.catalogProductType || 'Assembly',
                    catalogProductId: li.catalogProductId,
                    productListingId: li.productListingId || null,
                    quantity: li.quantity || 1,
                    displayLabel: li.displayLabel || null,
                    sortOrder: index,
                })),
        };

        this.setState({ saving: true });
        try {
            const service = ServiceLocator.Instance.getProductListingService();
            if (isNew) {
                const link = await service.createCart(payload);
                window.$alertSuccess('Cart created');
                this.props.history.replace(`/admin/customer-carts/${link.cartId}`);
                this.setState({ shareLink: link.loadUrl, saving: false });
            } else {
                await service.updateCart(cartId, payload);
                const refreshed = await service.getCart(cartId);
                window.$alertSuccess('Cart saved');
                this.setState({
                    cart: {
                        ...refreshed,
                        expiresUtc: refreshed.expiresUtc ? refreshed.expiresUtc.slice(0, 16) : '',
                        lineItems: refreshed.lineItems || [],
                    },
                    shareLink: refreshed.loadUrl || this.state.shareLink,
                    saving: false,
                });
            }
        } catch (err) {
            window.$alertError(err.message || 'Failed to save cart');
            this.setState({ saving: false });
        }
    };

    copyShareLink = () => {
        const { shareLink } = this.state;
        if (!shareLink) return;
        navigator.clipboard.writeText(shareLink).then(() => {
            window.$alertSuccess('Share link copied');
        }).catch(() => {
            window.$alertError('Could not copy link');
        });
    };

    render() {
        const { classes } = this.props;
        const { cartId } = this.props.match.params;
        const { loading, saving, refreshing, cart, shareLink, skuOptions } = this.state;
        const isNew = !cartId || cartId === 'new';
        const isCustomerCart = !!cart.isCustomerCart;
        const readOnly = isCustomerCart;
        const pageTitle = isNew
            ? 'New Customer Cart'
            : isCustomerCart
                ? `Live cart — ${cart.customerName || cart.customerEmail || cart.shareToken || ''}`
                : `Cart ${cart.shareToken || ''}`;

        if (loading) {
            return (
                <DashboardTemplate title="Customer Cart">
                    <div className="main-dashboard-root">
                        <center style={{ padding: 40 }}><CircularProgress /></center>
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="Customer Cart" onSyncDatabase={isCustomerCart ? () => this.refreshLiveCart() : undefined}>
                <div className="main-dashboard-root" style={{maxWidth: 1400, marginBottom: 20}}>
                    <div style={{ marginBottom: 0, marginTop: -10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10, position: 'relative' }}>
                            <Button
                                onClick={() => this.props.history.push('/admin/customer-carts')}
                                className="success-button-lg"
                            >
                                Back
                            </Button>
                            {isCustomerCart && (
                                refreshing ? (
                                    <CircularProgress size={28} />
                                ) : (
                                    <Button
                                        onClick={() => this.refreshLiveCart()}
                                        className="success-button-lg"
                                    >
                                        Refresh
                                    </Button>
                                )
                            )}
                            {saving ? (
                                <CircularProgress size={28} />
                            ) : !readOnly && (
                                <Button onClick={this.save} className="save-button-lg" style={{ backgroundColor: '#7fff84', color: 'black' }}>
                                    {isNew ? 'Create Cart' : 'Save Changes'}
                                </Button>
                            )}
                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                                <h1 style={{ margin: 0, color: 'white', fontSize: '1.5rem' }}>{pageTitle}</h1>
                            </div>
                        </div>
                    </div>

                    <div className="container" style={tileStyle}>
                        <h2 style={{ marginTop: 0 }}>Cart details</h2>
                        {isCustomerCart && (
                            <p style={{ color: '#1565c0', backgroundColor: '#e3f2fd', padding: '12px 16px', borderRadius: 6, marginTop: 0 }}>
                                This is a live storefront cart for <strong>{cart.customerName || cart.customerEmail || 'a customer'}</strong>.
                                It is read-only here and is removed automatically when the customer completes checkout.
                                {' '}Use Refresh (or wait up to ~15s) to pull the latest storefront contents.
                            </p>
                        )}
                        {isCustomerCart && (
                            <>
                                <TextField
                                    fullWidth margin="dense" variant="outlined" label="Customer"
                                    value={cart.customerName || ''}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    fullWidth margin="dense" variant="outlined" label="Email"
                                    value={cart.customerEmail || ''}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    fullWidth margin="dense" variant="outlined" label="Last updated"
                                    value={cart.updatedUtc ? new Date(cart.updatedUtc).toLocaleString() : ''}
                                    InputProps={{ readOnly: true }}
                                />
                            </>
                        )}
                        {!isCustomerCart && (
                            <>
                                <TextField
                                    fullWidth margin="dense" variant="outlined" label="Notes (internal)"
                                    value={cart.notes || ''}
                                    onChange={e => this.updateField('notes', e.target.value)}
                                />
                                <TextField
                                    fullWidth margin="dense" variant="outlined" label="Expires"
                                    type="datetime-local"
                                    value={cart.expiresUtc || ''}
                                    onChange={e => this.updateField('expiresUtc', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </>
                        )}
                        {shareLink && (
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                                <TextField fullWidth margin="dense" variant="outlined" label="Customer share link" value={shareLink} InputProps={{ readOnly: true }} />
                                <Button className="success-button-lg" onClick={this.copyShareLink} style={{ minWidth: 80, marginTop: 4 }}>Copy</Button>
                            </div>
                        )}
                        {isNew && (
                            <p style={{ color: '#666', marginBottom: 0 }}>
                                Save the cart to generate a shareable link for the customer site.
                            </p>
                        )}
                    </div>

                    <div className="container" style={tileStyle}>
                        <h2 style={{ marginTop: 0 }}>Line items</h2>
                        {!readOnly && (
                            <Button variant="contained" className="success-button-lg action-button" onClick={this.addLine} style={{ marginBottom: 16 }}>
                                Add line
                            </Button>
                        )}
                        {cart.lineItems.length === 0 && (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                {readOnly
                                    ? 'This cart is empty.'
                                    : <>No line items yet. Click <strong>Add line</strong> to add SKUs to this cart.</>}
                            </p>
                        )}
                        {readOnly ? cart.lineItems.map(line => (
                            <div key={line.id} className={classes.lineRow}>
                                <TextField
                                    label="SKU" margin="dense" variant="outlined" style={{ minWidth: 320, flex: 2 }}
                                    value={line.skuDisplay || line.displayLabel || line.description || line.catalogProductId}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="Qty" type="number" margin="dense" variant="outlined" style={{ width: 80 }}
                                    value={line.quantity || 1}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="Description" margin="dense" variant="outlined" style={{ flex: 1, minWidth: 180 }}
                                    value={line.description || line.displayLabel || ''}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="MSRP" margin="dense" variant="outlined" style={{ width: 100 }}
                                    value={line.msrp != null ? `$${Number(line.msrp).toFixed(2)}` : ''}
                                    InputProps={{ readOnly: true }}
                                />
                            </div>
                        )) : cart.lineItems.map(line => (
                            <div key={line.id} className={classes.lineRow}>
                                <Autocomplete
                                    autoHighlight
                                    style={{ minWidth: 320, flex: 2 }}
                                    className="autocomplete-dropdown"
                                    options={skuOptions}
                                    getOptionLabel={o => o.label || ''}
                                    getOptionSelected={(option, value) =>
                                        option
                                        && value
                                        && option.catalogProductId === value.catalogProductId
                                        && option.catalogProductType === value.catalogProductType
                                    }
                                    value={skuOptions.find(o =>
                                        o.catalogProductId === line.catalogProductId
                                        && o.catalogProductType === line.catalogProductType
                                    ) || null}
                                    onChange={(_, val) => {
                                        if (!val) return;
                                        this.updateLine(line.id, {
                                            catalogProductType: val.catalogProductType,
                                            catalogProductId: val.catalogProductId,
                                        });
                                    }}
                                    renderOption={option => (
                                        <PartAutocompleteOption option={option} classes={this.props.classes} />
                                    )}
                                    renderInput={params => (
                                        <TextField {...params} label="SKU" margin="dense" variant="outlined" />
                                    )}
                                />
                                <TextField
                                    label="Qty" type="number" margin="dense" variant="outlined" style={{ width: 80 }}
                                    value={line.quantity || 1}
                                    onChange={e => this.updateLine(line.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                                />
                                <TextField
                                    label="Display label" margin="dense" variant="outlined" style={{ flex: 1, minWidth: 180 }}
                                    value={line.displayLabel || ''}
                                    onChange={e => this.updateLine(line.id, { displayLabel: e.target.value })}
                                />
                                <IconButton onClick={() => this.removeLine(line.id)} aria-label="Remove line">
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

EditCustomerCart.propTypes = {
    classes: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditCustomerCart);
